import React from "react";
import { Box, Container, Typography, IconButton, Button } from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ConsultExperts from "./ConsultExperts";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@apollo/client";
import { CAMPAIGNS_PREVIEW_QUERY } from "../graphql/campaigns";
import { buildImageUrl } from "../config";

type Campaign = { id: string; name: string; image: string };

type BackendCampaign = {
  id: string;
  name: string;
  images: string[];
};

type GetCampaignsData = {
  campaignsPreview: BackendCampaign[];
};

const DiscoverCampaigns: React.FC = () => {
  const navigate = useNavigate();
  const { data } = useQuery<GetCampaignsData>(CAMPAIGNS_PREVIEW_QUERY);

  const campaigns: Campaign[] = React.useMemo(() => {
    return (data?.campaignsPreview ?? [])
      .map((c) => {
        const first = (c.images ?? [])[0];
        if (!first) return null;
        return { id: c.id, name: c.name, image: buildImageUrl(first) };
      })
      .filter(Boolean) as Campaign[];
  }, [data]);

  const N = campaigns.length;
  const [current, setCurrent] = React.useState(0);
  const [paused, setPaused] = React.useState(false);
  const [inView, setInView] = React.useState(false);
  const sectionRef = React.useRef<HTMLDivElement>(null);
  const touchStartX = React.useRef<number | null>(null);
  const touchStartY = React.useRef<number | null>(null);

  // Intersection Observer — pause autoplay when off screen
  React.useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => setInView(entry.isIntersecting),
      { threshold: 0.1 },
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  // Autoplay — runs every 3.5s when in view and not paused
  React.useEffect(() => {
    if (!N || paused || !inView) return;
    const id = setInterval(() => {
      setCurrent((i) => (i + 1) % N);
    }, 2500);
    return () => clearInterval(id);
  }, [N, paused, inView]);

  const goTo = (idx: number) => {
    setCurrent(((idx % N) + N) % N);
  };

  const prev = () => goTo(current - 1);
  const next = () => goTo(current + 1);

  // Touch swipe handlers
  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
    touchStartY.current = e.touches[0].clientY;
    setPaused(true);
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    if (touchStartX.current === null || touchStartY.current === null) return;
    const deltaX = touchStartX.current - e.changedTouches[0].clientX;
    const deltaY = touchStartY.current - e.changedTouches[0].clientY;
    if (Math.abs(deltaX) > Math.abs(deltaY) && Math.abs(deltaX) > 40) {
      if (deltaX > 0) {
        next();
      } else {
        prev();
      }
    }
    touchStartX.current = null;
    touchStartY.current = null;
    setPaused(false);
  };

  if (!N) return <ConsultExperts />;

  const prevIdx = (current - 1 + N) % N;
  const nextIdx = (current + 1) % N;

  return (
    <>
      <Box
        ref={sectionRef}
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
        sx={{
          py: { xs: 8, md: 12 },
          backgroundColor: "#F7F5F2",
          overflow: "hidden",
        }}
      >
        <Container maxWidth="lg">
          {/* Heading */}
          <Box sx={{ textAlign: "center", mb: { xs: 5, md: 7 } }}>
            <Typography
              component="h2"
              sx={{
                fontFamily: "Playfair Display, serif",
                fontWeight: 500,
                fontSize: { xs: "1.9rem", md: "2.8rem" },
                letterSpacing: "0.02em",
                color: "#111",
                textTransform: "uppercase",
                mb: 1.5,
              }}
            >
              Discover Our Campaigns
            </Typography>
            <Typography
              sx={{
                color: "rgba(0,0,0,0.55)",
                fontSize: { xs: "0.9rem", md: "1rem" },
                maxWidth: 560,
                mx: "auto",
                lineHeight: 1.7,
              }}
            >
              Explore signature campaigns starring our favourite muses — each
              story captures a distinct mood, refined styling, and timeless
              brilliance.
            </Typography>
          </Box>

          {/* Main Carousel */}
          <Box
            sx={{
              position: "relative",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: { xs: 1.5, md: 3 },
              minHeight: { xs: 260, md: 460 },
            }}
          >
            {/* Prev Slide */}
            <Box
              onClick={prev}
              sx={{
                cursor: "pointer",
                flex: "0 0 auto",
                width: { sm: 160, md: 240 },
                aspectRatio: "3/4",
                borderRadius: 2,
                overflow: "hidden",
                opacity: 0.45,
                transform: "scale(0.88)",
                transition: "all 0.5s ease",
                display: { xs: "none", sm: "block" },
                backgroundColor: "#e8e6e3",
              }}
            >
              {campaigns[prevIdx] && (
                <Box
                  component="img"
                  src={campaigns[prevIdx].image}
                  alt={campaigns[prevIdx].name}
                  loading="lazy"
                  sx={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                    display: "block",
                  }}
                />
              )}
            </Box>

            {/* Active Slide */}
            <Box
              onTouchStart={handleTouchStart}
              onTouchEnd={handleTouchEnd}
              sx={{
                flex: "0 0 auto",
                width: { xs: "85vw", sm: 260, md: 380 },
                maxWidth: 420,
                aspectRatio: "3/4",
                borderRadius: 3,
                overflow: "hidden",
                position: "relative",
                boxShadow: "0 24px 64px rgba(0,0,0,0.18)",
                backgroundColor: "#e8e6e3",
                touchAction: "pan-y",
              }}
            >
              {campaigns.map((c, idx) => (
                <Box
                  key={c.id}
                  component="img"
                  src={c.image}
                  alt={c.name}
                  loading={idx === 0 ? "eager" : "lazy"}
                  sx={{
                    position: "absolute",
                    inset: 0,
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                    display: "block",
                    opacity: idx === current ? 1 : 0,
                    transition: "opacity 0.6s ease",
                  }}
                />
              ))}

              {/* Gradient overlay */}
              <Box
                sx={{
                  position: "absolute",
                  inset: 0,
                  background:
                    "linear-gradient(to top, rgba(0,0,0,0.55) 0%, transparent 50%)",
                  pointerEvents: "none",
                  zIndex: 1,
                }}
              />

              {/* Campaign name on image */}
              <Box
                sx={{
                  position: "absolute",
                  bottom: 0,
                  left: 0,
                  right: 0,
                  p: { xs: 2, md: 3 },
                  color: "#fff",
                  pointerEvents: "none",
                  zIndex: 2,
                }}
              >
                <Typography
                  sx={{
                    fontFamily: `"Bodoni Moda", serif`,
                    fontWeight: 600,
                    fontSize: { xs: "1.1rem", md: "1.4rem" },
                    lineHeight: 1.2,
                    mb: 0.5,
                  }}
                >
                  {campaigns[current].name}
                </Typography>
                <Typography
                  sx={{
                    fontSize: { xs: "0.78rem", md: "0.85rem" },
                    color: "rgba(255,255,255,0.75)",
                    letterSpacing: "0.08em",
                    textTransform: "uppercase",
                  }}
                >
                  {current + 1} / {N}
                </Typography>
              </Box>
            </Box>

            {/* Next Slide */}
            <Box
              onClick={next}
              sx={{
                cursor: "pointer",
                flex: "0 0 auto",
                width: { sm: 160, md: 240 },
                aspectRatio: "3/4",
                borderRadius: 2,
                overflow: "hidden",
                opacity: 0.45,
                transform: "scale(0.88)",
                transition: "all 0.5s ease",
                display: { xs: "none", sm: "block" },
                backgroundColor: "#e8e6e3",
              }}
            >
              {campaigns[nextIdx] && (
                <Box
                  component="img"
                  src={campaigns[nextIdx].image}
                  alt={campaigns[nextIdx].name}
                  loading="lazy"
                  sx={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                    display: "block",
                  }}
                />
              )}
            </Box>
          </Box>

          {/* Controls */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: { xs: 2, md: 3 },
              mt: { xs: 3, md: 4 },
            }}
          >
            <IconButton
              onClick={prev}
              sx={{
                width: 44,
                height: 44,
                border: "1px solid rgba(0,0,0,0.2)",
                borderRadius: "50%",
                color: "#111",
                "&:hover": {
                  backgroundColor: "#111",
                  color: "#fff",
                  borderColor: "#111",
                },
                transition: "all 0.2s ease",
              }}
            >
              <ChevronLeftIcon />
            </IconButton>

            {/* Dots */}
            <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
              {campaigns.map((_, idx) => (
                <Box
                  key={idx}
                  onClick={() => goTo(idx)}
                  sx={{
                    width: idx === current ? 24 : 8,
                    height: 8,
                    borderRadius: 999,
                    backgroundColor:
                      idx === current ? "#111" : "rgba(0,0,0,0.2)",
                    cursor: "pointer",
                    transition: "all 0.3s ease",
                  }}
                />
              ))}
            </Box>

            <IconButton
              onClick={next}
              sx={{
                width: 44,
                height: 44,
                border: "1px solid rgba(0,0,0,0.2)",
                borderRadius: "50%",
                color: "#111",
                "&:hover": {
                  backgroundColor: "#111",
                  color: "#fff",
                  borderColor: "#111",
                },
                transition: "all 0.2s ease",
              }}
            >
              <ChevronRightIcon />
            </IconButton>
          </Box>

          {/* CTA */}
          <Box sx={{ textAlign: "center", mt: { xs: 4, md: 5 } }}>
            <Button
              variant="outlined"
              onClick={() => navigate("/campaigns")}
              sx={{
                px: { xs: 4, md: 6 },
                py: 1.4,
                borderRadius: 0,
                letterSpacing: "0.12em",
                fontSize: { xs: "0.78rem", md: "0.85rem" },
                textTransform: "uppercase",
                borderColor: "#111",
                color: "#111",
                fontWeight: 500,
                "&:hover": {
                  borderColor: "#111",
                  backgroundColor: "#111",
                  color: "#fff",
                },
                transition: "all 0.25s ease",
              }}
            >
              Explore All Campaigns
            </Button>
          </Box>
        </Container>
      </Box>
      <ConsultExperts />
    </>
  );
};

export default DiscoverCampaigns;
